import { useCallback, useEffect, useState } from 'react'

import { useModalScroll } from 'src/hooks/useModalScroll'
import { hasOpenOverlay } from 'src/helpers/openOverlay'

export interface UseFullscreenResult {
  isFullscreen: boolean
  toggle: () => void
  exit: () => void
}

/**
 * Full-screen state for a table surface, matching the game list's Details
 * sheet (`features/game/gameListView/components/DetailsPanel.tsx`). Shared by
 * the registration page and the competition Operations tab; each renders its
 * own `position: fixed` shell around the content while this is on.
 *
 * Two behaviours come with it, and both are load-bearing:
 *
 * - **Escape leaves.** The shell covers the whole window including the nav, so
 *   the toolbar button is otherwise the only way out. Bound in the CAPTURE
 *   phase on `window` so it fires wherever focus has landed, including inside
 *   an antd panel portalled to `document.body`.
 * - **The document is scroll-locked.** The page behind is still scrollable;
 *   left alone, a wheel event over the shell moves it, and exiting would drop
 *   the user somewhere they never navigated to.
 *
 * Deliberately NOT a URL param — registration's filters live in the address
 * bar because they change what you are looking at and a link should carry
 * them. This changes how much of one screen a table may take, which is a
 * preference of the moment; pinning it in a shared link would open someone
 * else's browser in a mode they did not ask for.
 */
export const useFullscreen = (): UseFullscreenResult => {
  'use memo'

  const [isFullscreen, setIsFullscreen] = useState(false)
  const { openModal, closeModal } = useModalScroll()

  const toggle = useCallback(() => setIsFullscreen((current) => !current), [])
  const exit = useCallback(() => setIsFullscreen(false), [])

  useEffect(() => {
    if (!isFullscreen) {
      return
    }
    openModal()
    return () => closeModal()
  }, [isFullscreen, openModal, closeModal])

  useEffect(() => {
    if (!isFullscreen) {
      return
    }
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') {
        return
      }
      /*
       * An open antd overlay owns the first Escape — a select, a dropdown, a
       * popover or a modal should close itself before the layout goes with it.
       * antd renders every one of them into a body portal, so a `contains()`
       * check on the shell would call an open panel "outside"; asking the
       * document whether any are open is what actually holds.
       */
      if (hasOpenOverlay()) {
        return
      }
      setIsFullscreen(false)
    }
    window.addEventListener('keydown', onKeyDown, true)
    return () => window.removeEventListener('keydown', onKeyDown, true)
  }, [isFullscreen])

  return { isFullscreen, toggle, exit }
}

export default useFullscreen
